import { useLocation, Link } from "react-router-dom";
import { ChevronLeft, Download } from "react-feather";
import { formatCurrency } from "../../../utils/formatters";
import { format } from "date-fns";
import { downloadPdf } from "../../../utils/pdf";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { UserAvatar } from "@/components/common/UserAvatar";
import { useBrandIdentity } from "../../../hooks/useBrandIdentity";
import type { BrandPayoutHistory } from "../../../services/payout.service";
import {
  buildPayoutInvoicePdf,
  fieldLabel,
  formatInvoiceDate as formatDate,
  paymentMethodsOf,
} from "./payoutInvoicePdf";

/*
 * The on-screen receipt card and the downloaded PDF show the same facts in
 * the same order; the PDF is drawn directly with pdf-lib (see
 * payoutInvoicePdf.ts) rather than screenshotted, so it is crisp and nothing
 * can drift out of place.
 */

function Row({
  label,
  value,
  strong,
}: {
  label: string;
  value: string;
  strong?: boolean;
}) {
  return (
    <div className="flex items-start justify-between gap-6 py-1.5 text-sm">
      <span className={strong ? "font-semibold text-foreground" : "text-muted-foreground"}>
        {label}
      </span>
      <span
        className={`text-right break-all ${
          strong ? "font-semibold text-foreground" : "font-medium text-foreground"
        }`}
      >
        {value}
      </span>
    </div>
  );
}

export function PayoutDetail() {
  const { state } = useLocation();
  const brand = useBrandIdentity();
  // The row is handed over from the history table; a direct visit or a
  // refresh has no state, so send the user back to the list instead of crashing.
  const payout = (state?.payout ?? null) as BrandPayoutHistory | null;

  if (!payout) {
    return (
      <EmptyState
        bordered={false}
        title="Payout not found"
        body="Open a payout from the history list to view its invoice"
        action={
          <Button asChild variant="outline">
            <Link to="/brand/payouts/history">Back to payout history</Link>
          </Button>
        }
      />
    );
  }

  const isPaid = payout.status === "PAID";
  const affiliateName = `${payout.affiliate.firstName} ${payout.affiliate.lastName}`.trim();
  const paidOn = payout.paidAt ?? payout.updatedAt;

  // The settled method (paid) or the methods on file that the brand can pay
  // with (pending). Each becomes one "method" row plus one row per field.
  const methods = paymentMethodsOf(payout);

  const handleDownloadInvoice = async () => {
    // The light-theme brand colour token ("r g b"), so the PDF matches the
    // brand on a white page whatever theme the screen is in.
    const brandColor = getComputedStyle(document.documentElement).getPropertyValue(
      "--brand-600-l"
    );
    const bytes = await buildPayoutInvoicePdf({
      payout,
      brandName: brand.name,
      brandColor,
    });
    downloadPdf(bytes, `payout-invoice-${payout.id}.pdf`);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Link
            to="/brand/payouts/history"
            className="text-muted-foreground hover:text-foreground"
          >
            <ChevronLeft className="w-5 h-5" />
          </Link>
          <h1 className="text-2xl font-semibold tracking-tight">
            Payout Invoice
          </h1>
        </div>
        {/* An invoice only exists once the payout has actually been paid. */}
        {isPaid && (
          <Button onClick={handleDownloadInvoice}>
            <Download className="w-4 h-4" />
            Download Invoice
          </Button>
        )}
      </div>

      <div className="mx-auto max-w-xl rounded-lg border border-border bg-card p-8">
        {/* Header: who issued it, what it is, and its state */}
        <div className="flex items-start justify-between gap-6">
          <div>
            {brand.name && (
              <p className="text-sm text-muted-foreground">{brand.name}</p>
            )}
            <h2 className="text-lg font-semibold">Payout invoice</h2>
          </div>
          <span
            className={`inline-block rounded-full px-3 text-xs font-medium leading-6 ${
              isPaid ? "bg-brand-100 text-brand-700" : "bg-muted text-muted-foreground"
            }`}
          >
            {isPaid ? "Paid" : "Pending"}
          </span>
        </div>

        {/* The one figure that matters */}
        <div className="mt-6">
          <p className="text-sm text-muted-foreground">
            {isPaid ? "Amount paid" : "Amount requested"}
          </p>
          <p className="text-3xl font-semibold tracking-tight">
            {formatCurrency(payout.amount)}
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            {isPaid
              ? `Paid on ${formatDate(paidOn)}`
              : `Requested on ${formatDate(payout.createdAt)}, awaiting payment`}
          </p>
        </div>

        {/* Recipient */}
        <div className="mt-6 border-t border-border pt-6">
          <p className="mb-3 text-sm text-muted-foreground">
            {isPaid ? "Paid to" : "Payable to"}
          </p>
          <div className="flex items-start gap-3">
            <UserAvatar
              src={payout.affiliate.profileImage}
              name={affiliateName}
              className="block h-12 w-12 shrink-0 text-center text-base leading-[3rem]"
            />
            <div className="min-w-0 pt-1">
              <p className="font-medium leading-5">{affiliateName}</p>
              <p className="break-all text-sm leading-5 text-muted-foreground">
                {payout.affiliate.email}
              </p>
            </div>
          </div>
        </div>

        {/* Reference facts, each stated once */}
        <div className="mt-6 border-t border-border pt-4">
          <Row label="Invoice number" value={payout.id} />
          <Row label="Requested on" value={formatDate(payout.createdAt)} />
          {isPaid && <Row label="Paid on" value={formatDate(paidOn)} />}
          {payout.referenceNumber && (
            <Row label="Payment reference" value={payout.referenceNumber} />
          )}
          {payout.code && <Row label="Affiliate code" value={payout.code} />}
        </div>

        {/* Where the money went (or can go) */}
        <div className="mt-4 border-t border-border pt-4">
          {methods.length === 0 ? (
            <Row label="Payment method" value="—" />
          ) : (
            methods.map((method, index) => (
              <div key={`${method.name}-${index}`}>
                <Row
                  label={methods.length > 1 ? `Payment method ${index + 1}` : "Payment method"}
                  value={method.name}
                />
                {Object.entries(method.fields).map(([key, value]) => (
                  <Row key={key} label={fieldLabel(key)} value={String(value)} />
                ))}
              </div>
            ))
          )}
        </div>

        {/* Amounts */}
        <div className="mt-4 border-t border-border pt-4">
          {payout.breakdown && payout.breakdown.recurringAmount > 0 && (
            <>
              <Row
                label="One-time commissions"
                value={formatCurrency(payout.breakdown.oneTimeAmount)}
              />
              <Row
                label={
                  payout.breakdown.renewalCount > 0
                    ? `Recurring commissions (${payout.breakdown.renewalCount} ${
                        payout.breakdown.renewalCount === 1 ? "renewal" : "renewals"
                      })`
                    : "Recurring commissions"
                }
                value={formatCurrency(payout.breakdown.recurringAmount)}
              />
            </>
          )}
          <Row label="Processing fee" value={formatCurrency(0)} />
          <div className="mt-2 border-t border-border pt-2">
            <Row
              label={isPaid ? "Total paid" : "Total due"}
              value={formatCurrency(payout.amount)}
              strong
            />
          </div>
        </div>

        <p className="mt-8 text-xs text-muted-foreground">
          Electronic invoice generated on {format(new Date(), "MMMM d, yyyy")}.
        </p>
      </div>
    </div>
  );
}
